import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormContext } from "react-hook-form";
import { addDays, format } from "date-fns";
import { IVariant } from "repoV2/features/Listing/Listing.interfaces";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { CHECKOUT_PAGES_ACTION_TYPES } from "repoV2/features/CheckoutPages/constants/CheckoutPagesReducer";
import { DAY_DATE_MONTH_FORMAT } from "repoV2/features/Common/modules/DateTime/modules/Time/Time.constants";
import useToastMessage from "repoV2/components/common/Toast/useToastMessage";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { checkMerchandiseShipping } from "./checkMerchandiseShipping";
import { useUserDetailsFormContext } from "../../UserDetailsFormGroup/modules/UserDetailsFormContextProvider";
import { getUserFormFieldName } from "../../UserDetailsFormGroup/UserDetailsFormGroup.utils";
import { checkout_form } from "../../../../CheckoutForm/CheckoutForm.constants";
import {
    default_shiprocket_data,
    merchandise_shiprocket_error,
    selected_merchandise_variant_form_key,
} from "../MerchandiseDetailsFormGroup.constants";

const {
    international_details: { field_names },
} = checkout_form;
const useCheckShiprocketPinCode = () => {
    const [isCheckingShiprocket, setIsCheckingShiprocket] =
        React.useState(false);
    const { getValues } = useFormContext();

    const { listingUuid } = useCheckoutBasicDetailsContext();
    const { userId } = useUserDetailsFormContext();

    const shiprocketData =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.merchandiseShiprocketDetails?.data
        ) || {};

    const { value = {} } = shiprocketData;

    const { shippingDays = "" } = value;

    const shippingDate = shippingDays
        ? format(addDays(new Date(), shippingDays), DAY_DATE_MONTH_FORMAT)
        : "";

    const { notify, toast_type_keys } = useToastMessage();
    const dispatch = useDispatch();

    const { variants = {} } =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.merchandiseVariantsDetails?.data
        ) || {};

    const isVariantMerchandise = Boolean(Object.keys(variants).length);

    const checkShiprocketPinCode = async ({
        currencyName,
        isInternationalEnabled,
    }: {
        currencyName: string;
        isInternationalEnabled: boolean;
    }) => {
        if (isCheckingShiprocket) {
            return;
        }

        setIsCheckingShiprocket(true);

        dispatch({
            type: CHECKOUT_PAGES_ACTION_TYPES.SET_MERCHANDISE_SHIPROCKET_DATA,
            payload: {
                listingUuid,
                data: {
                    ...shiprocketData,
                    ...default_shiprocket_data,
                },
            },
        });
        const data = getValues() || {};

        const pinCode =
            data[getUserFormFieldName(userId, field_names.postal_code)] || "";
        const variant: IVariant =
            data[selected_merchandise_variant_form_key] || {};

        if (!pinCode) {
            notify("Please enter Pin Code first!", {
                type: toast_type_keys.error,
            });
            setIsCheckingShiprocket(false);
            return;
        }

        if (isVariantMerchandise && !variant.uuid) {
            notify("Please select a variant to continue!", {
                type: toast_type_keys.error,
            });
            setIsCheckingShiprocket(false);
            return;
        }

        const response = await checkMerchandiseShipping({
            listingUuid,
            currencyName,
            isInternationalEnabled,
            pinCode,
            variantUuid: variant.uuid || "",
        });

        if (response?.isError) {
            dispatch({
                type: CHECKOUT_PAGES_ACTION_TYPES.SET_MERCHANDISE_SHIPROCKET_DATA,
                payload: {
                    listingUuid,
                    data: {
                        ...shiprocketData,
                        value: {},
                        isVerified: true,
                        error: merchandise_shiprocket_error,
                    },
                },
            });
        } else {
            dispatch({
                type: CHECKOUT_PAGES_ACTION_TYPES.SET_MERCHANDISE_SHIPROCKET_DATA,
                payload: {
                    listingUuid,
                    data: {
                        ...shiprocketData,
                        value: {
                            shippingCharge: response?.shipping_charge,
                            shippingDays: response?.shipping_days,
                        },
                        isVerified: true,
                        error: "",
                    },
                },
            });
        }
        setIsCheckingShiprocket(false);
    };

    return {
        isCheckingShiprocket,
        checkShiprocketPinCode,
        shiprocketData,
        shippingDate,
    };
};

export default useCheckShiprocketPinCode;
